// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/useAskV2ScriptedProgress.test.ts
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  askV2ReviewProgressThresholdSeed,
  buildAskV2ReviewProgressScript,
  generateAskV2ReviewProgressThresholdsMs,
  type AskV2ReviewProgressScriptPhase,
  type AskV2ReviewProgressScriptStepId,
  type AskV2ReviewProgressThresholdsMs,
} from "./buildAskV2ReviewProgressScript";
import { type AskV2ReviewScriptedProgressMode } from "./collectRailContent";
import { type AskV2RailProgressItem } from "./types";

const COMPLETED_VISIBLE_MS = 2000;
const TICK_MS = 1000;
const STORAGE_PREFIX = "ask-v2-rail-progress:";
const COMPLETED_FLAG_SUFFIX = ":completed";

type StoredProgressRun = {
  startedAt: number;
  thresholdsMs?: AskV2ReviewProgressThresholdsMs;
};

function resolveThresholdsMs(
  chatId: string | null | undefined,
  startedAt: number,
  stored: StoredProgressRun | null,
): AskV2ReviewProgressThresholdsMs {
  if (stored?.thresholdsMs?.length === 4) {
    return stored.thresholdsMs;
  }
  const seed = askV2ReviewProgressThresholdSeed(
    chatId?.trim() ?? "",
    startedAt,
  );
  return generateAskV2ReviewProgressThresholdsMs(seed);
}

function readStoredRun(storageKey: string | null): StoredProgressRun | null {
  if (storageKey == null || typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredProgressRun;
    if (typeof parsed.startedAt !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredRun(
  storageKey: string | null,
  run: StoredProgressRun | null,
): void {
  if (storageKey == null || typeof sessionStorage === "undefined") return;
  if (run == null) {
    sessionStorage.removeItem(storageKey);
    return;
  }
  sessionStorage.setItem(storageKey, JSON.stringify(run));
}

export function askV2ScriptedProgressStorageKey(
  chatId: string | null | undefined,
): string | null {
  const trimmed = chatId?.trim();
  return trimmed ? `${STORAGE_PREFIX}${trimmed}` : null;
}

function completedFlagKey(storageKey: string | null): string | null {
  return storageKey ? `${storageKey}${COMPLETED_FLAG_SUFFIX}` : null;
}

function readCompletedFlag(storageKey: string | null): boolean {
  const key = completedFlagKey(storageKey);
  if (key == null || typeof sessionStorage === "undefined") return false;
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

export function clearAskV2ScriptedProgressCompleted(
  chatId: string | null | undefined,
): void {
  writeCompletedFlag(askV2ScriptedProgressStorageKey(chatId), false);
}

function writeCompletedFlag(
  storageKey: string | null,
  completed: boolean,
): void {
  const key = completedFlagKey(storageKey);
  if (key == null || typeof sessionStorage === "undefined") return;
  if (completed) {
    sessionStorage.setItem(key, "1");
    return;
  }
  sessionStorage.removeItem(key);
}

function initialScriptedProgressState(
  mode: AskV2ReviewScriptedProgressMode,
  storageKey: string | null,
  chatId: string | null | undefined,
): {
  phase: AskV2ReviewProgressScriptPhase;
  startedAt: number | null;
  elapsedMs: number;
  thresholdsMs: AskV2ReviewProgressThresholdsMs;
} {
  const idleThresholds = generateAskV2ReviewProgressThresholdsMs(0);
  if (mode === "idle") {
    return {
      phase: "idle",
      startedAt: null,
      elapsedMs: 0,
      thresholdsMs: idleThresholds,
    };
  }
  if (mode === "completed" || readCompletedFlag(storageKey)) {
    if (mode === "completed") {
      writeCompletedFlag(storageKey, true);
    }
    return {
      phase: "completed",
      startedAt: null,
      elapsedMs: 0,
      thresholdsMs: idleThresholds,
    };
  }
  const stored = readStoredRun(storageKey);
  const startedAt = stored?.startedAt ?? Date.now();
  const thresholdsMs = resolveThresholdsMs(chatId, startedAt, stored);
  if (stored == null) {
    writeStoredRun(storageKey, { startedAt, thresholdsMs });
  }
  return {
    phase: "running",
    startedAt,
    elapsedMs: Math.max(0, Date.now() - startedAt),
    thresholdsMs,
  };
}

export function useAskV2ScriptedProgress(input: {
  chatId: string | null | undefined;
  mode: AskV2ReviewScriptedProgressMode;
}): {
  items: AskV2RailProgressItem[];
  showShell: boolean;
} {
  const { t } = useTranslation("ui");
  const storageKey = askV2ScriptedProgressStorageKey(input.chatId);
  const labels = useMemo(
    () =>
      ({
        "script-plan": t("askV2.rail.progressSteps.plan"),
        "script-load": t("askV2.rail.progressSteps.load"),
        "script-agents": t("askV2.rail.progressSteps.agents"),
        "script-inspect": t("askV2.rail.progressSteps.inspect"),
        "script-comments": t("askV2.rail.progressSteps.comments"),
      }) satisfies Record<AskV2ReviewProgressScriptStepId, string>,
    [t],
  );

  const [phase, setPhase] = useState<AskV2ReviewProgressScriptPhase>(
    () =>
      initialScriptedProgressState(input.mode, storageKey, input.chatId).phase,
  );
  const [startedAt, setStartedAt] = useState<number | null>(
    () =>
      initialScriptedProgressState(input.mode, storageKey, input.chatId)
        .startedAt,
  );
  const [elapsedMs, setElapsedMs] = useState(
    () =>
      initialScriptedProgressState(input.mode, storageKey, input.chatId)
        .elapsedMs,
  );
  const [thresholdsMs, setThresholdsMs] =
    useState<AskV2ReviewProgressThresholdsMs>(
      () =>
        initialScriptedProgressState(input.mode, storageKey, input.chatId)
          .thresholdsMs,
    );
  const completedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasRunningRef = useRef(input.mode === "running");

  useEffect(() => {
    if (input.mode === "completed") {
      if (completedTimerRef.current != null) {
        clearTimeout(completedTimerRef.current);
        completedTimerRef.current = null;
      }
      writeStoredRun(storageKey, null);
      writeCompletedFlag(storageKey, true);
      setStartedAt(null);
      setElapsedMs(0);
      setPhase("completed");
      wasRunningRef.current = false;
      return;
    }

    if (input.mode === "running") {
      if (completedTimerRef.current != null) {
        clearTimeout(completedTimerRef.current);
        completedTimerRef.current = null;
      }
      if (readCompletedFlag(storageKey)) {
        // why: stale in-flight signals land before history/reviews hydrate on an
        // old chat; keep the completed shell until mode flips for a new review.
        setPhase("completed");
        return;
      }
      writeCompletedFlag(storageKey, false);
      const stored = readStoredRun(storageKey);
      const nextStartedAt = stored?.startedAt ?? Date.now();
      const nextThresholds = resolveThresholdsMs(
        input.chatId,
        nextStartedAt,
        stored,
      );
      if (stored == null) {
        writeStoredRun(storageKey, {
          startedAt: nextStartedAt,
          thresholdsMs: nextThresholds,
        });
      }
      setStartedAt(nextStartedAt);
      setElapsedMs(Math.max(0, Date.now() - nextStartedAt));
      setThresholdsMs(nextThresholds);
      setPhase("running");
      wasRunningRef.current = true;
      return;
    }

    if (input.mode === "idle") {
      if (wasRunningRef.current) {
        wasRunningRef.current = false;
        writeStoredRun(storageKey, null);
        setStartedAt(null);
        setElapsedMs(0);
        setPhase("completed");
        if (completedTimerRef.current != null) {
          clearTimeout(completedTimerRef.current);
        }
        completedTimerRef.current = setTimeout(() => {
          setPhase("idle");
          completedTimerRef.current = null;
        }, COMPLETED_VISIBLE_MS);
        return;
      }

      if (completedTimerRef.current != null) {
        clearTimeout(completedTimerRef.current);
        completedTimerRef.current = null;
      }
      writeStoredRun(storageKey, null);
      setStartedAt(null);
      setElapsedMs(0);
      setPhase("idle");
    }
  }, [input.mode, storageKey]);

  useEffect(() => {
    if (phase !== "running" || startedAt == null) return undefined;

    const tick = () => {
      setElapsedMs(Math.max(0, Date.now() - startedAt));
    };
    tick();
    const intervalId = setInterval(tick, TICK_MS);
    return () => clearInterval(intervalId);
  }, [phase, startedAt]);

  useEffect(
    () => () => {
      if (completedTimerRef.current != null) {
        clearTimeout(completedTimerRef.current);
      }
    },
    [],
  );

  const items = useMemo(
    () =>
      buildAskV2ReviewProgressScript({
        elapsedMs,
        phase,
        labels,
        thresholdsMs,
      }),
    [elapsedMs, labels, phase, thresholdsMs],
  );

  const showShell = input.mode === "completed" || phase !== "idle";

  return { items, showShell };
}
